import type { Account } from './account';
import type { ConstValues } from './helpers';
import type { InstanceName } from './instance';

export const UserRole = {
  ADMIN: 'corporate.admin',
  ADVERTISER_ADMIN: 'advertiser.admin',
  ADVERTISER_ADMIN_RESTRICTED: 'advertiser.admin.restricted',
  ADVERTISER_EXCHANGE_BUYER: 'advertiser.exchange_buyer',
  ADVERTISER_REPORTING: 'advertiser.reporting',
  MARKET_OPERATOR: 'market.operator',
  NETWORK_ADMIN: 'network.admin',
  NETWORK_DEMAND_ONLY: 'network.admin.demand',
  NETWORK_SUPPLY_ONLY: 'network.admin.supply',
  NETWORK_AUDIENCE_SHARE: 'network.audienceshare',
  NETWORK_INVENTORY_CURATOR_OPS: 'network.inventory_curator_ops',
  NETWORK_OA_INTERNAL: 'network.oa_internal',
  NETWORK_OA_OPENX_BUYER: 'network.oa_openx_buyer',
  NETWORK_OA_BUYER: 'network.oa_buyer',
  NETWORK_IDFREE_USER: 'network.idfree_user',
  NETWORK_IDFREE_ADMIN: 'network.idfree_admin',
  NETWORK_PMP_USER: 'network.pmp_user',
  NETWORK_REPORTING: 'network.reporting',
  PD: 'corporate.pd_user',
  ROOT: 'root',
  PUBLISHER_ADMIN: 'publisher.admin',
  PUBLISHER_REPORTING: 'publisher.reporting',
  CRT_ACCESS: 'crt_reviewer',
  DEAL_FORECASTING_USER: 'deal_forecasting.user',
  DEAL_REVENUE_CALCULATOR: 'deal_revenue_calculator.user',
  DEAL_TROUBLESHOOTING_USER: 'deal_troubleshooting.user',
  NETWORK_ADMIN_DEMAND_RESTRICTED: 'network.admin.demand.restricted',
  MIGRATED_CUSTOM_ROLE: 'reporting',
  NETWORK_SSP_DEMO: 'network.ssp.demo',
  DIRECT_DEALS_VIEWER: 'publisher_marketplace.viewer',
  XPERT_VIEWER: 'xpert_viewer',
  CORPORATE_SWITCH: 'corporate.switch',
  PUBLISHER_TC_ACCESS: 'publisher.tc_access',
  AI_INVENTORY_DISCOVERY: 'ai_enabled_inventory_discovery.user',
  AQUA: 'aqua.user',
  AMAZON_MARKETPLACE_DEAL: 'amazon_marketplace_deal',
  DEAL_AD_CATEGORY_PROPOSAL_REVIEWER: 'deal_ad_category_proposal.reviewer',
} as const;
export type UserRole = ConstValues<typeof UserRole>;

export const UserStatus = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
} as const;
export type UserStatus = ConstValues<typeof UserStatus>;

export const CORPORATE_SSO_REALMS = ['test_corporate_xv', 'test_corporate', 'openx_corporate'];

export type User = {
  id: string;
  first_name: string;
  last_name: string;
  uid: string;
  created_date: string;
  modified_date: string;
  default_report_range: string;
  email: string;
  name: string;
  notes: string;
  locale: string;
  external_id: string;
  workas_role: UserRole;
  roles: UserRole[];
  role?: UserRole;
  status: UserStatus;
  instance_uid: string;
  instance_name: InstanceName | string;
  can_work_as: boolean;
  compiled_acl: string;
  account?: Account;
  account_id: string;
  account_uid: string;
  partners: string[];
  service_region: string[];
};

export interface RealUser {
  email: string;
  realm: string;
  roles: UserRole[];
  work_as: boolean;
  work_as_self: boolean;
}

export interface UserTableData {
  id: string;
  name: string;
  email: string;
  status: UserStatus;
  hierarchy: string;
  role: string;
}

export type UserList = Pick<User, 'id' | 'first_name' | 'last_name' | 'created_date'> & {
  name?: string;
  account?: Pick<Account, 'id' | 'name'>;
  email_address: string;
  is_active?: boolean;
  last_signin_time?: string | null;
  role?: string;
};

export type UsersRouteParams = {
  userId?: string;
};
